import React, { useState } from "react";
import PropTypes from "prop-types";
import Card from "shared/Card";

import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import InputItem from "Events/Components/InputItem";
import TextArea from "shared/@forms/TextArea";
import Button from "shared/Button";
import AddButton from "Events/Components/AddButton";
import styles from "./ADAV2Form.module.css";

import { ImagePicker } from "Events/Components/ImagePicker/ImagePicker";
import SelectPhotoModal from "../SelectPhotoModal";
import { Plus } from "react-ikonate";
import classNames from "classnames";

export function ADAV2Form({ entries, setEntries, pageMode = "edit" }) {
  const getFieldValue = (field) => {
    if (!field) return "";
    return field.saved ?? field.value ?? "";
  };

  const handleTextAreaChange = (newValue, entry) => {
    const newEntries = entries.map((e) =>
      e.id === entry.id
        ? {
            ...e,
            param1: {
              ...e.param1,
              saved: newValue,
            },
          }
        : e
    );
    setEntries(newEntries);
  };

  // const handleIconChange = (icon, entry) => {
  //   if (!icon || typeof icon === "string") {
  //     // Handle existing Base64 string or null values directly
  //     const newEntries = entries.map((e) =>
  //       e.id === entry.id
  //         ? {
  //             ...e,
  //             icon: {
  //               ...e.icon,
  //               saved: icon || e.icon?.saved || null,
  //             },
  //           }
  //         : e
  //     );
  //     // setEntries(newEntries); //Calls on page load - base
  //     return;
  //   }

  //   // If it's a file, convert it to Base64
  //   const convertToBase64 = (file) => {
  //     return new Promise((resolve, reject) => {
  //       if (!(file instanceof Blob)) {
  //         reject(new TypeError("The provided file is not a Blob or File."));
  //         return;
  //       }
  //       const reader = new FileReader();
  //       reader.onload = () => resolve(reader.result);
  //       reader.onerror = (error) => reject(error);
  //       reader.readAsDataURL(file);
  //     });
  //   };

  //   convertToBase64(icon)
  //     .then((base64Image) => {
  //       const newEntries = entries.map((e) =>
  //         e.id === entry.id
  //           ? {
  //               ...e,
  //               icon: {
  //                 ...e.icon,
  //                 saved: base64Image,
  //               },
  //             }
  //           : e
  //       );
  //       setEntries(newEntries);
  //     })
  //     .catch((error) => {
  //       console.error("Error converting icon to Base64:", error);
  //     });
  // };

  const [showIconModalId, setShowIconModalId] = useState(null);

  const handleSubmitIcon = (iconUrl, entry) => {
    if (!iconUrl) return;

    const newEntries = entries.map((e) =>
      e.id === entry.id
        ? {
            ...e,
            icon: {
              ...e.icon,
              saved: iconUrl,
            },
          }
        : e
    );

    setEntries(newEntries);
    setShowIconModalId(null);
  };

  const handleInputChange = (newValue, paramName, entryId) => {
    const updatedEntry = entries.find((entry) => entry.id === entryId);
    const updatedParam = {
      ...updatedEntry[paramName],
      saved: newValue,
    };

    setEntries(
      entries.map((e) =>
        e.id === entryId
          ? {
              ...e,
              [paramName]: updatedParam,
            }
          : e
      )
    );
  };

  const handleOrderChange = (sourceIndex, destinationIndex) => {
    if (sourceIndex === destinationIndex || destinationIndex == null) return;

    const entriesClone = [...entries];

    // Identify the description entry
    const descriptionIndex = entriesClone.findIndex(
      (entry) =>
        (entry.component?.saved || entry.component?.value) === "description"
    );

    // Prevent moving the description entry
    if (
      sourceIndex === descriptionIndex ||
      destinationIndex === descriptionIndex
    ) {
      console.warn("Description entry order cannot be changed.");
      return;
    }

    // Remove the dragged item from the array
    const [movedItem] = entriesClone.splice(sourceIndex, 1);

    // Insert the dragged item at the new index
    entriesClone.splice(destinationIndex, 0, movedItem);

    // Reassign order values and update `order.saved` for all entries
    const updatedEntries = entriesClone.map((entry, index) => ({
      ...entry,
      order: {
        ...entry.order,
        saved: index, // Update the `order.saved` field
      },
    }));

    setEntries(updatedEntries);
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return; // If dropped outside a valid drop zone
    handleOrderChange(source.index, destination.index);
  };

  const addNew = () => {
    // Temporary entry until backend assigns an ID
    const newEntry = {
      component: { saved: "Checkbox" },
      icon: { saved: null },
      order: { saved: entries.length },
      param1: { saved: null },
      param2: { saved: "" },
      id: null,
    };

    // Update state with the new entry
    setEntries([...entries, newEntry], true);
  };

  const addADA = () => {
    setEntries(
      [
        {
          ...entries[0],
          param1: {
            ...entries[0].param1,
            saved: entries[0].param1.value ?? "",
          },
        },
        {
          component: { saved: "Checkbox" },
          icon: { saved: null },
          order: { saved: entries.length },
          param1: { saved: null },
          param2: { saved: "" },
          id: null,
        },
      ],
      true
    );
  };

  const removeEntry = (entry) => {
    const newEntries = entries.filter((e) => e.id !== entry.id);
    setEntries(newEntries);
  };

  const removeAllEntries = () => {
    const description = entries.filter(
      (e) => (e.component?.saved || e.component?.value) === "description"
    );
    const newEntries = description.map((e) => ({
      ...e,
      param1: { ...e.param1, saved: null },
    }));
    setEntries(newEntries);
  };

  function capitalizeFirstLetter(str) {
    if (!str) return "";
    if (str == "asl") return "ASL";
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  const isReadOnly = pageMode === "view";
  const placeholderText = isReadOnly ? "" : "Click to type";
  const requiredMark = isReadOnly ? "" : " *";

  const renderParams = (entry) => {
    const isMobility =
      entry.component?.saved === "mobility" ||
      entry.component?.value === "mobility";
    const label1 = isReadOnly
      ? isMobility
        ? "Option 1"
        : "Text"
      : isMobility
      ? "Option 1"
      : entry.component?.saved === "checkbox" ||
        entry.component?.value === "checkbox"
      ? "Text"
      : "Option 1";
    const label2 = isReadOnly
      ? isMobility
        ? "Option 2"
        : "Sub-Text"
      : isMobility
      ? "Option 2"
      : entry.component?.saved === "checkbox" ||
        entry.component?.value === "checkbox"
      ? "Sub-Text"
      : "Option 2";
    if (
      (entry.component?.saved || entry.component?.value) === "description" &&
      entries.length !== 1
    ) {
      return (
        <div className={styles.paramContainer}>
          <div className={styles.paramItemDescription}>
            <div className={styles.type}></div>
            <div className={styles.iconContainer} />
            <div className={styles.fieldDescription}>
              <TextArea
                className={`${styles.textArea} ${
                  isReadOnly ? styles.readOnly : ""
                }`}
                value={getFieldValue(entry.param1)}
                onInput={(e) => handleTextAreaChange(e.target.value, entry)}
                placeholder={placeholderText}
                style={isReadOnly ? { pointerEvents: "none" } : {}}
              />
            </div>
          </div>
        </div>
      );
    } else if (
      entry.component?.saved === "mobility" ||
      entry.component?.value === "mobility"
    ) {
      return (
        <div className={styles.paramContainer}>
          <div className={styles.paramItem}>
            <div className={styles.type}>
              {label1} {entry?.param1?.required ? requiredMark : ""}{" "}
            </div>
            {/* <div className={styles.iconContainer}>
              <ImagePicker
                changeImageItem={(icon) => handleIconChange(icon, entry)}
                isADA={true}
                imgSrc={getFieldValue(entry.icon)}
                disabled={isReadOnly}
              />
            </div> */}
            <div className={styles.iconContainer}>
              {entry.icon?.saved ? (
                <img
                  src={entry.icon.saved}
                  alt="Selected"
                  className={styles.icon}
                  onClick={() => setShowIconModalId(entry.id)}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setShowIconModalId(entry.id)}
                  className={classNames(styles.imagePickerUploadButtonSmall)}
                >
                  <Plus
                    className={styles.imagePickerPlusSmall}
                    borderwidth={5}
                  />
                </button>
              )}

              {showIconModalId === entry.id && (
                <SelectPhotoModal
                  isVisible={showIconModalId === entry.id}
                  setIsVisible={() => setShowIconModalId(null)}
                  onSubmit={(icon) => handleSubmitIcon(icon, entry)}
                  folder="icons"
                />
              )}
            </div>
            <div className={styles.field}>
              <input
                className={`${styles.fieldInput} ${
                  isReadOnly ? styles.readOnly : ""
                }`}
                placeholder={placeholderText}
                value={getFieldValue(entry.param1)}
                onChange={(e) =>
                  handleInputChange(e.target.value, "param1", entry.id)
                }
                readOnly={isReadOnly}
                style={isReadOnly ? { pointerEvents: "none" } : {}}
              />
            </div>
          </div>
          <div className={styles.paramItem}>
            <div className={styles.type}>
              {label2} {entry?.param2?.required ? requiredMark : ""}{" "}
            </div>
            <div className={styles.iconContainer} />
            <div className={styles.field}>
              <input
                className={`${styles.fieldInput} ${
                  isReadOnly ? styles.readOnly : ""
                }`}
                placeholder={placeholderText}
                value={getFieldValue(entry.param2)}
                onChange={(e) =>
                  handleInputChange(e.target.value, "param2", entry.id)
                }
                readOnly={isReadOnly}
                style={isReadOnly ? { pointerEvents: "none" } : {}}
              />
            </div>
          </div>
        </div>
      );
    } else if (
      entry.component?.saved == "checkbox" ||
      entry.component?.value == "checkbox"
    ) {
      return (
        <div className={styles.paramContainer}>
          <div className={styles.paramItem}>
            <div className={styles.type}>
              Text {entry?.param1?.required ? requiredMark : ""}{" "}
            </div>
            <div className={styles.iconContainer}>
              {entry.icon?.saved ? (
                <img
                  src={entry.icon.saved}
                  alt="Selected"
                  className={styles.icon}
                  onClick={() => setShowIconModalId(entry.id)}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setShowIconModalId(entry.id)}
                  className={classNames(styles.imagePickerUploadButtonSmall)}
                >
                  <Plus
                    className={styles.imagePickerPlusSmall}
                    borderwidth={5}
                  />
                </button>
              )}

              {showIconModalId === entry.id && (
                <SelectPhotoModal
                  isVisible={showIconModalId === entry.id}
                  setIsVisible={() => setShowIconModalId(null)}
                  onSubmit={(icon) => handleSubmitIcon(icon, entry)}
                  folder="icons"
                />
              )}
            </div>
            <div className={styles.field}>
              <input
                className={`${styles.fieldInput} ${
                  isReadOnly ? styles.readOnly : ""
                }`}
                placeholder={placeholderText}
                value={getFieldValue(entry.param1)}
                onChange={(e) =>
                  handleInputChange(e.target.value, "param1", entry.id)
                }
                readOnly={isReadOnly}
                style={isReadOnly ? { pointerEvents: "none" } : {}}
              />
            </div>
          </div>
          <div className={styles.paramItem}>
            <div className={styles.type}>Sub-Text</div>
            <div className={styles.iconContainer} />
            <div className={styles.field}>
              <input
                className={`${styles.fieldInput} ${
                  isReadOnly ? styles.readOnly : ""
                }`}
                placeholder={placeholderText}
                value={getFieldValue(entry.param2)}
                onChange={(e) =>
                  handleInputChange(e.target.value, "param2", entry.id)
                }
                readOnly={isReadOnly}
                style={isReadOnly ? { pointerEvents: "none" } : {}}
              />
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className={styles.paramContainer}>
          <div className={styles.paramItem}>
            <div className={styles.type}>
              Text {entry.param1?.required ? " *" : ""}{" "}
            </div>
            <div className={styles.iconContainer}>
              {entry.icon?.saved ? (
                <img
                  src={entry.icon.saved}
                  alt="Selected"
                  className={styles.icon}
                  onClick={() => setShowIconModalId(entry.id)}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setShowIconModalId(entry.id)}
                  className={classNames(styles.imagePickerUploadButtonSmall)}
                >
                  <Plus
                    className={styles.imagePickerPlusSmall}
                    borderwidth={5}
                  />
                </button>
              )}

              {showIconModalId === entry.id && (
                <SelectPhotoModal
                  isVisible={showIconModalId === entry.id}
                  setIsVisible={() => setShowIconModalId(null)}
                  onSubmit={(icon) => handleSubmitIcon(icon, entry)}
                  folder="icons"
                />
              )}
            </div>
            <div className={styles.field}>
              <input
                className={`${styles.fieldInput} ${
                  isReadOnly ? styles.readOnly : ""
                }`}
                placeholder={placeholderText}
                value={getFieldValue(entry.param1)}
                onChange={(e) =>
                  handleInputChange(e.target.value, "param1", entry.id)
                }
                readOnly={isReadOnly}
                style={isReadOnly ? { pointerEvents: "none" } : {}}
              />
            </div>
          </div>
          <div className={styles.paramItem}>
            <div className={styles.type}>Sub-Text</div>
            <div className={styles.iconContainer} />
            <div className={styles.field}>
              <input
                className={`${styles.fieldInput} ${
                  isReadOnly ? styles.readOnly : ""
                }`}
                placeholder={placeholderText}
                value={getFieldValue(entry.param2)}
                onChange={(e) =>
                  handleInputChange(e.target.value, "param2", entry.id)
                }
                readOnly={isReadOnly}
                style={isReadOnly ? { pointerEvents: "none" } : {}}
              />
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <DragDropContext onDragEnd={isReadOnly ? () => {} : onDragEnd}>
      <Card
        children={
          <section className={styles.container}>
            <div className={styles.title}>ADA</div>
            <hr className={styles.hr} />
            <Droppable droppableId="entries">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={styles.entriesContainer}
                >
                  {entries
                    .slice()
                    .sort((a, b) => a.order - b.order)
                    .filter(
                      (item) =>
                        (item?.component?.saved || item?.component?.value) !==
                          "description" || entries.length !== 1
                    )
                    .map((entry, index) => (
                      <Draggable
                        key={entry.id}
                        draggableId={entry.id?.toString()}
                        index={index}
                        isDragDisabled={
                          isReadOnly ||
                          (entry.component?.saved || entry.component?.value) ===
                            "description"
                        }
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <div className={styles.entry}>
                              <div className={styles.entryTitle}>
                                {capitalizeFirstLetter(
                                  entry.component?.saved ??
                                    entry.component?.value
                                )}{" "}
                                {(entry.component?.saved ||
                                  entry.component?.value) === "description" &&
                                !isReadOnly
                                  ? "*"
                                  : ""}
                              </div>
                              <div className={styles.entryParamsContainer}>
                                {renderParams(entry)}
                              </div>
                              {pageMode !== "view" && (
                                <div
                                  className={styles.entryRemoveButtonContainer}
                                >
                                  {(entry.component?.saved ||
                                    entry.component?.value) !==
                                  "description" ? (
                                    <>
                                      {entries.length > 2 && (
                                        <Button
                                          children="Remove"
                                          className={styles.removeButton}
                                          onClick={() => removeEntry(entry)}
                                          size="small"
                                        />
                                      )}
                                    </>
                                  ) : (
                                    <Button
                                      children="Remove ADA"
                                      className={styles.removeADAButton}
                                      onClick={() => removeAllEntries()}
                                      size="small"
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                            <hr className={styles.hr} />
                          </div>
                        )}
                      </Draggable>
                    ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
            {pageMode !== "view" && (
              <AddButton
                description={
                  entries.length !== 1 ? "Add another ADA Option" : "Add ADA"
                }
                color="#9AB5BB"
                onClick={() => (entries.length !== 1 ? addNew() : addADA())}
              />
            )}
          </section>
        }
      />
    </DragDropContext>
  );
}

ADAV2Form.propTypes = {
  /**Array of objects in the format:
   * [
   *    {
   *        question: "do you require x y z?",
   *        options: ["yes", "no", "etc"],
   *        id: 1 (unique id per question),
   *    }, and so on
   * ]
   */
  entries: PropTypes.arrayOf(PropTypes.shape({})),
  /**Function to handle setting the entries */
  setEntries: PropTypes.func,
  pageMode: PropTypes.string,
};
